import modalStyle from "../modal.module.css";
import buttonStyle from "../../buttons.module.css";

import { Trash2, Sparkles, X, NotebookPen } from "lucide-react";

import {
  apiNetworkRequestVulnerabilityAssociationDelete,
  apiNetworkRequestPentestVulnerabilityList,
  apiCommandVulnerabilityAssociationsDelete,
  apiCommandsPentestVulnerabilityList,
} from "../../../../sdk.ts";
import { useState, useEffect, useRef, useCallback, useContext } from "react";

import { convertCommandsToIO, convertRequestsToIO } from "../../../Common/IOBlockInterface.tsx";
import CommandBlock from "../../../PentestWindows/components/CommandLogger/components/CommandWidget/components/CommandList/components/CommandBlock/CommandBlock.tsx";
import axios from "axios";
import { PentestContext } from "../../../../contexts/PentestContext.ts";
import { CommandConnection, IOBlock, NetworkRequestConnection, Vulnerability } from "../../../../sdkStructs.ts";

interface Props {
  showVuln: Vulnerability;

  token: string;
  pentestId: number;

  orderNumber: number;
  generateSingleVuln?: () => void;

  closeModal: (refresh: boolean) => void;
}

async function fetchCommandsByVuln(token: string, pentestId: number, vulnId: number) {
  try {
    const result = await apiCommandsPentestVulnerabilityList(token, pentestId, vulnId);
    return convertCommandsToIO(result.data);
  } catch (error) {
    if (!axios.isAxiosError(error)) {
      return [];
    }
    console.log("ERROR FETCHING COMMANDS BY VULN:", error.response);
    return [];
  }
}
async function fetchRequestsByVuln(token: string, pentestId: number, vulnId: number) {
  try {
    const result = await apiNetworkRequestPentestVulnerabilityList(token, pentestId, vulnId);
    return convertRequestsToIO(result.data);
  } catch (error) {
    if (!axios.isAxiosError(error)) {
      return [];
    }
    console.log("ERROR FETCHING REQUESTS BY VULN:", error.response);
    return [];
  }
}

function VulnModal({ showVuln, token, pentestId, closeModal, generateSingleVuln, orderNumber }: Readonly<Props>) {
  const [IOList, setIOList] = useState<IOBlock[]>([]);

  const [localLoading, setLocalLoading] = useState(true);

  const { setCurrentWindow, scrollToVuln, confirmRemoveVuln } = useContext(PentestContext);

  const refreshIOList = useCallback(async () => {
    if (!showVuln) return;
    const io_commands: IOBlock[] = await fetchCommandsByVuln(token, pentestId, showVuln.ID);
    const io_requests: IOBlock[] = await fetchRequestsByVuln(token, pentestId, showVuln.ID);

    const final_io: IOBlock[] = io_commands.concat(io_requests);
    // Apply sorting order
    final_io.sort((a, b) => {
      if (a.timestamp < b.timestamp) return 1;
      return a.timestamp > b.timestamp ? -1 : 0;
    });

    setIOList(final_io);
    setLocalLoading(false);
  }, [pentestId, showVuln, token, setLocalLoading]);

  const resetState = useCallback(
    (refresh: boolean) => {
      setIOList([]);
      closeModal(refresh);
    },
    [closeModal],
  );

  useEffect(() => {
    const dialog = dialogRef?.current;
    if (dialog) {
      dialog.showModal();
      dialog.addEventListener("close", () => {
        resetState(false);
      });
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", () => {
          resetState(false);
        });
      }
    };
  }, [resetState]);

  async function removeConnection(element: IOBlock) {
    if (!showVuln) return;

    try {
      if (element.type === "command") {
        const association: CommandConnection = {
          vulnerability_id: showVuln.ID,
          command_id: element.id,
        };
        await apiCommandVulnerabilityAssociationsDelete(token, association);
      } else {
        const association: NetworkRequestConnection = {
          vulnerability_id: showVuln.ID,
          network_request_id: element.id,
        };
        await apiNetworkRequestVulnerabilityAssociationDelete(token, association);
      }
      refreshIOList();
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR DELETING ASSOCIATION:", error.response, element);
    }
  }

  function getVulnName() {
    if (!showVuln) return "Undefined";

    if (showVuln.Name !== "") return showVuln.Name;
    return "Unnamed Vuln";
  }

  function getVulnTag() {
    if (!showVuln.Tag) return "Undefined";

    if (showVuln.Tag !== "") return showVuln.Tag;
    return "RandomTag";
  }

  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    async function load() {
      dialogRef.current?.showModal();
      await refreshIOList();
    }

    if (dialogRef === null) {
      alert("Vuln modal not found!");
      return;
    }
    load();
  }, [showVuln, refreshIOList]);

  function renderVulnList() {
    if (localLoading) return <h4>Loading ...</h4>;
    if (IOList.length === 0)
      return <h5 className={modalStyle.text}>There are no commands linked with this vulnerability.</h5>;

    return IOList.map((command: IOBlock) => (
      <CommandBlock
        key={command.id}
        data={command}
        selectFun={removeConnection}
        selected={false}
        reportMode={false}
        standalone={false}
      />
    ));
  }

  return (
    <dialog ref={dialogRef} className={modalStyle.vulnModalWrap + " " + modalStyle.modalStyle}>
      <div className={modalStyle.vulnRow}>
        <div className={modalStyle.modalDeleteButton}>
          <Trash2
            className={modalStyle.modalCloseIcon}
            stroke="var(--light-blue-icon)"
            strokeWidth="1.5"
            onClick={() => {
              confirmRemoveVuln(showVuln.ID);
            }}
          />
        </div>
        <h2 className={modalStyle.vulnName}>{getVulnName()}</h2>
        {showVuln.Tag && <h2 className={modalStyle.vulnTag}> {getVulnTag()} </h2>}
        <div className={modalStyle.modalCloseButton}>
          <X className={modalStyle.modalCloseIcon} stroke="white" strokeWidth="1.5" onClick={() => resetState(false)} />
        </div>
      </div>
      <div className={modalStyle.vulnColumn}>{renderVulnList()}</div>
      <div className={modalStyle.actionButtons}>
        <button
          className={buttonStyle.button}
          onClick={() => {
            resetState(false);
            if (generateSingleVuln) generateSingleVuln();
          }}
        >
          <Sparkles className={buttonStyle.iconMargin} stroke="white" strokeWidth="1.5" />
          {!showVuln.Description ? "Generate" : "Regenerate"}
        </button>
        <button
          className={buttonStyle.button + " " + buttonStyle.greenButton}
          onClick={() => {
            setCurrentWindow("editor");
            setTimeout(() => scrollToVuln(orderNumber - 1), 2000);
            closeModal(false);
          }}
        >
          <NotebookPen className={buttonStyle.iconMargin} stroke="white" strokeWidth="1.5" />
          Editor
        </button>
      </div>
    </dialog>
  );
}

export default VulnModal;
